import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApplicationButton,
  ApplicationContextualHelp,
} from '../../components/application';

const COPY = {
  ready: 'Your account has been locked and you can no longer sign in. We’re completing the account-closure process.',
  pending: 'Your account has been locked and you can no longer sign in. We’re completing the account-closure process.',
  completed: 'You can no longer sign in with this account. Some information may remain temporarily in restricted form where required.',
  needs_attention: 'Account closure needs attention. This status does not restore account access.',
  uncertain: 'Status is temporarily unavailable. The closure process may continue. Your recovery information has been kept; you can check again.',
  unavailable: 'This browser can no longer retrieve account-closure status. The closure process may continue; losing recovery information does not cancel it. V1 does not provide replacement recovery credentials or another status-recovery method.',
  expired: 'Status access has expired. This does not cancel account closure, delete its records, or restore account access.',
  storage_unavailable: 'Recovery information could not be saved. No account-closure initiation request was sent by this action.',
  removal_failed: 'Local recovery information could not be removed. You can try again.',
};

const HELP_TITLE = 'About account closure';
const HELP_RETENTION = 'Account closure may take some time to complete. Some information may need to be kept temporarily in restricted form for legal reasons, but it will no longer be used for ordinary All-Voiced purposes.';
const HELP_RECOVERY = 'This browser has saved information that allows you to check the progress of the closure. Clearing that information does not cancel the account closure.';
const REMOVE_EXPLANATION = 'This removes the information stored in this browser for checking the closure status. It does not cancel or change the account closure.';

// No auth requirement, User lookup, or initiation on render. Only the shared
// confirmed-deletion modal calls client.initiate explicitly.
export default function LifecycleRecoveryView({ client, initialState = 'ready' }) {
  const [state, setState] = useState(initialState);
  const [busy, setBusy] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const heading = useRef(null);
  const run = async action => {
    setBusy(true);
    try { const result = await action(); setState(result.state); }
    catch (_) { setState('uncertain'); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    heading.current?.focus();
    if (initialState === 'ready' && typeof client?.recover === 'function') {
      void run(client.recover);
    }
  // A fresh recovery surface has no current projection, so read authoritative
  // status once. A state supplied by the accepted initiation/recovery flow is
  // already authoritative and is rendered without a redundant request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const title = state === 'completed'
    ? 'Your account has been closed'
    : state === 'storage_unavailable'
      ? 'Account closure was not started'
      : 'Your account is being closed';
  const completed = state === 'completed';
  return <section aria-labelledby="closure-status-heading" aria-busy={busy}>
    <h2 ref={heading} tabIndex={-1} id="closure-status-heading">{title}</h2>
    <p role="status" aria-live="polite">{COPY[state] || COPY.uncertain}</p>
    {!completed && <ApplicationButton disabled={busy} onClick={() => run(client.recover)}>Refresh status</ApplicationButton>}
    <Link
      className={`av-app-button av-app-button--${completed ? 'primary' : 'secondary'}`}
      to="/"
    >
      Go to All-Voiced
    </Link>
    <ApplicationContextualHelp label={HELP_TITLE} title={HELP_TITLE}>
      <p>{HELP_RETENTION}</p>
      <p>{HELP_RECOVERY}</p>
      <p>{REMOVE_EXPLANATION}</p>
      <ApplicationButton variant="quiet" disabled={busy} onClick={() => setConfirmRemoval(true)}>
        Remove closure status from this browser
      </ApplicationButton>
      {confirmRemoval && <div>
        <p>{REMOVE_EXPLANATION}</p>
        <ApplicationButton variant="destructive" disabled={busy} onClick={() => run(async () => {
          const result = await client.forget(); setConfirmRemoval(false); heading.current?.focus(); return result;
        })}>Confirm removal</ApplicationButton>
        <ApplicationButton variant="quiet" disabled={busy} onClick={() => setConfirmRemoval(false)}>Cancel</ApplicationButton>
      </div>}
    </ApplicationContextualHelp>
  </section>;
}
